import {observer} from "mobx-react-lite";
import {
    Accordion, AccordionDetails, AccordionSummary, Button,
    Checkbox,
    Dialog, DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Paper,
    Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, {useEffect, useState} from "react";
import {Section} from "../../../../entity/resource/section";
import {Document} from "../../../../entity/resource/document";
import {DocumentDto} from "../../../../entity/dto/title-template-dto";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {ValidateDocumentDialogProps} from "../../../../entity/props/title/validate-document-dialog-props";

import '../../../../assets/css/style.css';

const ValidateDocumentDialog = (props: ValidateDocumentDialogProps): JSX.Element => {

    const [sectionAccordion, setSectionAccordion] = useState<boolean>(true);
    const [selectAll, setSelectAll] = useState<boolean>(false);
    const [selectedSections, setSelectedSections] = useState<Map<string, string[]>>(new Map());
    const [expandedSection, setExpandedSection] = useState<number | null>(null);

    useEffect(() => {
        setSelectAll(true);
        handleSelectAll();
    }, []);

    const handleSelectAll = (): void => {
        if (selectAll)
            setSelectedSections(new Map());
        else {
            const sectionMap = new Map(props.selectedTitle.sections.map((section: Section): [string, string[]] => [section.id, section.documents.map((document: Document): string => document.id)]));
            setSelectedSections(sectionMap);
        }

        setSelectAll(!selectAll);
    }

    const handleSectionFilter = (sectionId: string, documents: DocumentDto[]): void => {
        const updatedMap = new Map(selectedSections);

        if (selectedSections.has(sectionId)) {
            updatedMap.delete(sectionId);
            setSelectedSections(updatedMap);
        } else {
            const documentIds: string[] = documents.map((document: DocumentDto): string => document.id);
            updatedMap.set(sectionId, documentIds);
        }

        setSelectedSections(updatedMap);
    }

    const isSelectedDocument = (sectionId: string, documentId: string): boolean => {
        if (selectedSections.has(sectionId)) {
            const documents = selectedSections.get(sectionId) ?? [];
            return documents.includes(documentId);
        }

        return false;
    }

    const handleDocumentFilter = (event: any, section: Section, documentId: string): void => {
        event.stopPropagation();

        const documentIds = selectedSections.get(section.id) as string[] ?? [];
        const updatedMap = new Map(selectedSections);

        const updatedDocumentIds = documentIds.includes(documentId) ? documentIds.filter((id: string): boolean => id !== documentId) : [...documentIds, documentId];
        if (updatedDocumentIds.length > 0)
            updatedMap.set(section.id, updatedDocumentIds);
        else
            updatedMap.delete(section.id);

        setSelectedSections(updatedMap);
    }

    const renderSection = (section: Section, index: number): JSX.Element => {
        const hasDocuments = section.documents && section.documents.length > 0;
        const isExpanded = expandedSection === index;

        return (
            <Accordion
                key={index}
                expanded={isExpanded}
                className={`template-list-item ${index % 2 === 0 ? 'even' : 'odd'} ${index === props.selectedTitle.sections.length - 1 ? 'last' : ''} ${isExpanded ? 'parent' : ''}`}>
                <AccordionSummary expandIcon={ hasDocuments ? <ExpandMoreIcon onClick={(): void => setExpandedSection(expandedSection === index ? null : index)}/> : null}>
                    <Box className={'checkbox-template-container parent template-checkbox'}>
                        <Checkbox
                            checked={selectedSections.has(section.id)}
                            onClick={() => handleSectionFilter(section.id, [...section.documents.map((document: Document): DocumentDto => ({ id: document.id, name: document.name, url: document.url}))])}/>
                        <Typography className={'dialog-icon'}>{section.name}</Typography>
                    </Box>
                </AccordionSummary>
                {
                    hasDocuments &&
                    <AccordionDetails>
                        {
                            section.documents.map((document: Document): JSX.Element => renderDocument(section, document.id, document.name, document.url))
                        }
                    </AccordionDetails>
                }
            </Accordion>
        );
    }

    const renderDocument = (section: Section, documentId: string, documentName: string, documentUrl: string): JSX.Element => {
        return (
            <Box className={'template-list-item child'}>
                <Box className={'checkbox-template-container template-checkbox'} onClick={(): Window | null => window.open(documentUrl)}>
                    <Checkbox checked={isSelectedDocument(section.id, documentId)} onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => handleDocumentFilter(event, section, documentId)}/>
                    <PictureAsPdfIcon className={'zitles-icon checkbox-template-container icon'}/>
                    <Typography className={'book-and-page-header'}>{documentName}</Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Dialog open={props.open}>
            <DialogTitle className={'zitles-dialog-title'}>
                <span>Validate Documents</span>
                <IconButton onClick={(): void => props.setOpen(false)}>
                    <CloseIcon className={'dialog-icon'}/>
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Paper elevation={0}>
                    <Accordion
                        elevation={0}
                        className={'template-dialog-card'}
                        expanded={sectionAccordion}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon onClick={(): void => setSectionAccordion(!sectionAccordion)} className={'dialog-icon'}/>}>
                            <Typography className={'template-data-value accordion'}>Sections and Documents</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box className={'checkbox-template-container header template-checkbox'}>
                                <Checkbox checked={selectAll} onClick={handleSelectAll}/>
                                <Typography className={'checkbox-template-container title'}>{ selectAll ? 'Deselect All' : 'Select All'}</Typography>
                            </Box>
                            {
                                props.selectedTitle.sections.filter((section: Section): boolean => section.name !== 'Unclassified').map(renderSection)
                            }
                        </AccordionDetails>
                    </Accordion>
                </Paper>
            </DialogContent>
            <DialogActions className={'action-btn-container'}>
                <Box>
                    <Button
                        disableTouchRipple
                        onClick={(): void => props.setOpen(false)}
                        className={'zitles-button cancel'}>
                        Cancel
                    </Button>
                    <Button
                        disabled={Array.from(selectedSections.values()).flat().length < 1}
                        className={`zitles-button template submit ${Array.from(selectedSections.values()).flat().length < 1 ? 'disable' : ''}`}
                        onClick={(): void => props.handleSubmit(Array.from(selectedSections.values()).flat())}>
                        Submit
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}

export default observer(ValidateDocumentDialog);
